/**
 * Family Billing Management Page
 * Epic 86: Family Subscription Management
 *
 * Displays subscription status, child management, and billing controls
 * for family subscriptions (parent role)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Tile,
  Button,
  Tag,
  ProgressBar,
  Modal,
  TextInput,
  NumberInput,
  RadioButtonGroup,
  RadioButton,
  Loading,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Checkbox,
} from '@carbon/react';
import { Add, TrashCan, ChevronRight } from '@carbon/icons-react';
import './FamilyBilling.scss';

interface Subscription {
  id: string;
  plan_name: string;
  status: string;
  billing_interval: string;
  price: number;
  currency: string;
  current_period_end: number;
  child_count: number;
  cancel_at_period_end: boolean;
  features: string[];
}

interface Child {
  user_id: string;
  name: string;
  created_at: number;
  parent_consent_given: boolean;
  oversight_level: string;
}

interface SubscriptionData {
  subscription: Subscription | null;
  children: Child[];
  children_count: number;
  child_limit: number;
}

// Module-level cache to prevent duplicate fetches across remounts
let dataCache: SubscriptionData | null = null;
let isFetching = false;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 1 minute
let mountCounter = 0;

const FamilyBillingComponent: React.FC = () => {
  const componentId = React.useRef(++mountCounter);
  const [subscription, setSubscription] = useState<Subscription | null>(dataCache?.subscription || null);
  const [children, setChildren] = useState<Child[]>(dataCache?.children || []);
  const [childrenCount, setChildrenCount] = useState(dataCache?.children_count || 0);
  const [childLimit, setChildLimit] = useState(dataCache?.child_limit || 3);
  const [loading, setLoading] = useState(!dataCache);
  const [processing, setProcessing] = useState(false);

  // Add Child Modal
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState(10);
  const [gradeLevel, setGradeLevel] = useState('5');
  const [coppaConsent, setCoppaConsent] = useState(false);

  // Remove Child Modal
  const [removeChildModalOpen, setRemoveChildModalOpen] = useState(false);
  const [childToRemove, setChildToRemove] = useState<Child | null>(null);
  const [dataRetention, setDataRetention] = useState<'archive' | 'delete'>('archive');

  // Upgrade Modal
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Track mount/unmount lifecycle
  React.useEffect(() => {
    console.log(`[FamilyBilling #${componentId.current}] MOUNTED`);
    return () => {
      console.log(`[FamilyBilling #${componentId.current}] UNMOUNTED`);
    };
  }, []);

  const fetchSubscription = useCallback(async () => {
    console.log(`[FamilyBilling #${componentId.current}] fetchSubscription called`);
    const now = Date.now();

    // Use cached data if available and fresh
    if (dataCache && now - lastFetchTime < CACHE_DURATION) {
      console.log('[FamilyBilling] Using cached data');
      setSubscription(dataCache.subscription);
      setChildren(dataCache.children);
      setChildrenCount(dataCache.children_count);
      setChildLimit(dataCache.child_limit);
      setLoading(false);
      return;
    }

    // If another instance is fetching, wait for it
    if (isFetching) {
      console.log('[FamilyBilling] Fetch already in progress, waiting...');
      const startWait = Date.now();
      while (isFetching && Date.now() - startWait < 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (dataCache) {
        console.log('[FamilyBilling] Using data from concurrent fetch');
        setSubscription(dataCache.subscription);
        setChildren(dataCache.children);
        setChildrenCount(dataCache.children_count);
        setChildLimit(dataCache.child_limit);
        setLoading(false);
        return;
      }
    }

    isFetching = true;
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      if (!token) {
        console.error('[FamilyBilling] No authentication token found');
        setLoading(false);
        return;
      }

      console.log('[FamilyBilling] Fetching from /api/v1/subscriptions/family/current');
      const response = await fetch('/api/v1/subscriptions/family/current', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('[FamilyBilling] API response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[FamilyBilling] API error:', errorText);
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[FamilyBilling] Data received:', data);

      if (data.has_subscription) {
        setSubscription(data.subscription);
        setChildren(data.children || []);
        setChildrenCount(data.children_count || 0);
        setChildLimit(data.child_limit || 3);
        dataCache = {
          subscription: data.subscription,
          children: data.children || [],
          children_count: data.children_count || 0,
          child_limit: data.child_limit || 3,
        };
        lastFetchTime = Date.now();
      } else {
        setSubscription(null);
        setChildren([]);
        setChildrenCount(0);
        setChildLimit(3);
        dataCache = { subscription: null, children: [], children_count: 0, child_limit: 3 };
        lastFetchTime = Date.now();
      }
    } catch (error) {
      console.error('[FamilyBilling] Error fetching subscription:', error);
      setSubscription(null);
      setChildren([]);
      setChildrenCount(0);
      setChildLimit(3);
      dataCache = { subscription: null, children: [], children_count: 0, child_limit: 3 };
      lastFetchTime = Date.now();
    } finally {
      console.log('[FamilyBilling] Setting loading to false');
      setLoading(false);
      isFetching = false;
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleAddChild = async () => {
    setProcessing(true);
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      const response = await fetch('/api/v1/subscriptions/family/add-child', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: childName,
          age: childAge,
          grade_level: gradeLevel,
          coppa_consent: childAge < 13 ? coppaConsent : false,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAddChildModalOpen(false);
        setChildName('');
        setChildAge(10);
        setGradeLevel('5');
        setCoppaConsent(false);
        // Clear cache to force refetch
        dataCache = null;
        fetchSubscription();
      } else if (data.upgrade_required) {
        // Show upgrade modal
        setAddChildModalOpen(false);
        setUpgradeModalOpen(true);
        alert(data.message);
      } else {
        console.error('Failed to add child:', data.error || data.message);
        alert(data.error || data.message);
      }
    } catch (error) {
      console.error('Failed to add child:', error);
      alert('Failed to add child');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveChild = async () => {
    if (!childToRemove) return;

    setProcessing(true);
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      const response = await fetch(`/api/v1/subscriptions/family/remove-child/${childToRemove.user_id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data_retention: dataRetention }),
      });

      const data = await response.json();

      if (data.success) {
        setRemoveChildModalOpen(false);
        setChildToRemove(null);
        // Clear cache to force refetch
        dataCache = null;
        fetchSubscription();
      }
    } catch (error) {
      console.error('Failed to remove child:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <Loading description="Loading subscription..." withOverlay={false} />;
  }

  if (!subscription) {
    return (
      <div className="family-billing">
        <Tile className="no-subscription-tile">
          <h2 className="cds--type-heading-04">No Active Family Subscription</h2>
          <p>Subscribe to unlock AI tutoring for your children with parental controls and COPPA compliance.</p>
          <Button kind="primary" renderIcon={Add} onClick={() => (window.location.href = '/pricing')}>
            View Family Plans
          </Button>
        </Tile>
      </div>
    );
  }

  const statusTagType =
    subscription.status === 'active'
      ? 'green'
      : subscription.status === 'trialing'
        ? 'blue'
        : subscription.status === 'past_due'
          ? 'red'
          : 'gray';

  const nextBillingDate = new Date(subscription.current_period_end * 1000).toLocaleDateString();
  const childUsagePercentage = childLimit > 0 ? (childrenCount / childLimit) * 100 : 0;
  const childrenRemaining = childLimit - childrenCount;

  return (
    <div className="family-billing">
      <h1 className="cds--type-heading-05">Family Subscription & Billing</h1>

      {/* Subscription Status Card */}
      <Tile className="subscription-status-tile">
        <div className="subscription-header">
          <div>
            <h3 className="cds--type-heading-02">{subscription.plan_name}</h3>
            <Tag type={statusTagType}>{subscription.status.toUpperCase()}</Tag>
          </div>
          <div className="subscription-price">
            ${subscription.price}/{subscription.billing_interval}
          </div>
        </div>

        {/* Child Usage */}
        <div className="child-usage">
          <p className="cds--type-body-01">
            {childrenCount} of {childLimit} children active
          </p>
          <ProgressBar
            label="Child Slots"
            value={childUsagePercentage}
            max={100}
            helperText={`${childrenRemaining} slots remaining`}
          />
        </div>

        <div className="billing-info">
          <p>Next billing date: {nextBillingDate}</p>
          {subscription.cancel_at_period_end && (
            <p className="cancellation-notice">⚠️ Subscription will cancel on {nextBillingDate}</p>
          )}
        </div>

        <div className="subscription-actions">
          <Button
            kind="primary"
            onClick={() => setAddChildModalOpen(true)}
            disabled={processing || childrenCount >= childLimit}
          >
            {childrenCount >= childLimit ? 'Upgrade to Add More' : 'Add Child'}
          </Button>
          {childrenCount >= childLimit && (
            <Button kind="secondary" onClick={() => setUpgradeModalOpen(true)} disabled={processing}>
              Upgrade Plan
            </Button>
          )}
        </div>
      </Tile>

      {/* Children List Card */}
      <Tile className="children-list-tile">
        <h3 className="cds--type-heading-02">Your Children</h3>
        {children.length > 0 ? (
          <StructuredListWrapper>
            <StructuredListHead>
              <StructuredListRow head>
                <StructuredListCell head>Name</StructuredListCell>
                <StructuredListCell head>Added</StructuredListCell>
                <StructuredListCell head>COPPA Consent</StructuredListCell>
                <StructuredListCell head>Actions</StructuredListCell>
              </StructuredListRow>
            </StructuredListHead>
            <StructuredListBody>
              {children.map(child => (
                <StructuredListRow key={child.user_id}>
                  <StructuredListCell>{child.name}</StructuredListCell>
                  <StructuredListCell>{new Date(child.created_at * 1000).toLocaleDateString()}</StructuredListCell>
                  <StructuredListCell>{child.parent_consent_given ? 'Yes' : 'No'}</StructuredListCell>
                  <StructuredListCell>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      iconDescription="Remove child"
                      hasIconOnly
                      onClick={() => {
                        setChildToRemove(child);
                        setRemoveChildModalOpen(true);
                      }}
                      disabled={processing}
                    />
                  </StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        ) : (
          <p className="cds--type-body-01">No children added yet. Click "Add Child" to get started.</p>
        )}
      </Tile>

      {/* Features Card */}
      <Tile className="features-tile">
        <h3 className="cds--type-heading-02">Your Plan Includes</h3>
        <ul className="features-list">
          {subscription.features.map((feature, index) => (
            <li key={index}>
              <ChevronRight size={16} /> {feature}
            </li>
          ))}
        </ul>
      </Tile>

      {/* Add Child Modal */}
      <Modal
        open={addChildModalOpen}
        modalHeading="Add Child to Family Subscription"
        primaryButtonText={processing ? 'Adding...' : 'Add Child'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setAddChildModalOpen(false)}
        onRequestSubmit={handleAddChild}
        primaryButtonDisabled={processing || !childName.trim() || (childAge < 13 && !coppaConsent)}
      >
        <p>Add your child to the family subscription to give them access to AI tutoring.</p>
        <TextInput
          id="child-name"
          labelText="Child's Name"
          value={childName}
          onChange={e => setChildName(e.target.value)}
        />
        <NumberInput
          id="child-age"
          label="Age"
          min={5}
          max={18}
          value={childAge}
          onChange={(_e, { value }) => setChildAge(typeof value === 'number' ? value : parseInt(value, 10) || 10)}
        />
        <TextInput
          id="grade-level"
          labelText="Grade Level"
          value={gradeLevel}
          onChange={e => setGradeLevel(e.target.value)}
        />
        {childAge < 13 && (
          <Checkbox
            labelText="I give parental consent for this child (required for COPPA compliance)"
            id="coppa-consent"
            checked={coppaConsent}
            onChange={(_e, { checked }) => setCoppaConsent(checked)}
          />
        )}
        <p className="cds--type-caption-01" style={{ marginTop: '1rem' }}>
          Slots remaining: {childrenRemaining}
        </p>
      </Modal>

      {/* Remove Child Modal */}
      <Modal
        open={removeChildModalOpen}
        modalHeading="Remove Child from Subscription"
        primaryButtonText={processing ? 'Removing...' : 'Remove Child'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setRemoveChildModalOpen(false)}
        onRequestSubmit={handleRemoveChild}
        danger
        primaryButtonDisabled={processing}
      >
        {childToRemove && (
          <>
            <p>Are you sure you want to remove {childToRemove.name} from your family subscription?</p>
            <RadioButtonGroup
              legendText="What should we do with their data?"
              name="data-retention"
              valueSelected={dataRetention}
              onChange={value => setDataRetention(value as 'archive' | 'delete')}
            >
              <RadioButton labelText="Archive for 30 days (recommended)" value="archive" id="data-archive" />
              <RadioButton labelText="Delete immediately (COPPA right to delete)" value="delete" id="data-delete" />
            </RadioButtonGroup>
          </>
        )}
      </Modal>

      {/* Upgrade Modal */}
      <Modal
        open={upgradeModalOpen}
        modalHeading="Upgrade Your Family Plan"
        primaryButtonText="Go to Pricing"
        secondaryButtonText="Cancel"
        onRequestClose={() => setUpgradeModalOpen(false)}
        onRequestSubmit={() => (window.location.href = '/pricing')}
      >
        <p>You've reached your child limit on your current plan.</p>
        <p>Upgrade to a higher tier to add more children:</p>
        <ul>
          <li>Family Basic: 3 children - $19.99/mo</li>
          <li>Family Plus: 5 children - $29.99/mo</li>
          <li>Family Unlimited: Unlimited children - $39.99/mo</li>
        </ul>
      </Modal>
    </div>
  );
};

// Wrap in React.memo to prevent unnecessary remounts
export const FamilyBilling = React.memo(FamilyBillingComponent);

export default FamilyBilling;
